import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../api/axios'
import { Alert } from '../../../components/ui/Alert'
import { Modal } from '../../../components/ui/Modal'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { FormField, inputClass } from '../../../components/ui/FormField'
import { btnPrimario, btnSecundario, cartao, erroApi, td, th } from './comum'

// Pistas (stages) da etapa IPSC

export interface Pista {
  idpista: number
  idetapa: number
  numero: number
  nome: string
  descricao: string | null
  tiros_minimos: number
  pontos_maximos: number
  ativo: boolean
  resultados: number
}
type Form = { numero: string; nome: string; descricao: string; tiros_minimos: string; pontos_maximos: string; ativo: boolean }

export function usePistas(idetapa: number) {
  return useQuery({
    queryKey: ['ipsc-pistas', idetapa],
    queryFn: () => api.get<{ data: Pista[] }>(`/ipsc/etapas/${idetapa}/pistas`).then((r) => r.data.data),
  })
}

export function AbaPistas({ idetapa }: { idetapa: number }) {
  const qc = useQueryClient()
  const { data: pistas = [] } = usePistas(idetapa)
  const [editando, setEditando] = useState<Pista | 'nova' | null>(null)
  const [excluir, setExcluir] = useState<Pista | null>(null)
  const [form, setForm] = useState<Form>({ numero: '1', nome: '', descricao: '', tiros_minimos: '', pontos_maximos: '', ativo: true })
  const [alerta, setAlerta] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const atualizar = () => {
    qc.invalidateQueries({ queryKey: ['ipsc-pistas', idetapa] })
    qc.invalidateQueries({ queryKey: ['ipsc-etapas'] })
  }
  const salvar = useMutation({
    mutationFn: (dados: object) => (editando && editando !== 'nova' ? api.put(`/ipsc/pistas/${editando.idpista}`, dados) : api.post(`/ipsc/etapas/${idetapa}/pistas`, dados)),
    onSuccess: () => { atualizar(); setEditando(null); setAlerta({ type: 'success', message: 'Pista salva' }) },
    onError: (e) => setAlerta({ type: 'error', message: erroApi(e, 'Erro ao salvar a pista') }),
  })
  const apagar = useMutation({
    mutationFn: (p: Pista) => api.delete(`/ipsc/pistas/${p.idpista}`),
    onSuccess: () => { atualizar(); setAlerta({ type: 'success', message: 'Pista excluída' }) },
    onError: (e) => setAlerta({ type: 'error', message: erroApi(e, 'Erro ao excluir a pista') }),
    onSettled: () => setExcluir(null),
  })

  function abrir(p: Pista | 'nova') {
    setEditando(p)
    setForm(p === 'nova'
      ? { numero: String((pistas.at(-1)?.numero ?? 0) + 1), nome: '', descricao: '', tiros_minimos: '', pontos_maximos: '', ativo: true }
      : { numero: String(p.numero), nome: p.nome, descricao: p.descricao ?? '', tiros_minimos: String(p.tiros_minimos), pontos_maximos: String(p.pontos_maximos || ''), ativo: p.ativo })
  }
  const maxUsado = (p: Pick<Pista, 'pontos_maximos' | 'tiros_minimos'>) => (p.pontos_maximos > 0 ? p.pontos_maximos : p.tiros_minimos * 5)

  return (
    <div className="space-y-3">
      {alerta && <Alert type={alerta.type} message={alerta.message} onClose={() => setAlerta(null)} />}
      <div className="flex justify-between items-center gap-3">
        <p className="text-sm text-compumaq-textoSuave">
          Cada pista tem os seus pontos máximos (vazio = tiros mínimos × 5). Na apuração, o maior Hit Factor da divisão em cada pista leva os pontos máximos.
        </p>
        <button className={btnPrimario} onClick={() => abrir('nova')}>+ Nova pista</button>
      </div>
      <div className={`${cartao} overflow-x-auto`}>
        {pistas.length === 0 ? (
          <p className="text-center py-12 text-compumaq-textoSuave">Nenhuma pista cadastrada nesta etapa.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
              <tr><th className={th}>Nº</th><th className={th}>Pista</th><th className={th}>Tiros mín.</th><th className={th}>Pontos máx.</th><th className={th}>Resultados</th><th className={th}></th></tr>
            </thead>
            <tbody className="divide-y divide-compumaq-dourado/25">
              {pistas.map((p) => (
                <tr key={p.idpista} className={p.ativo ? '' : 'opacity-50'}>
                  <td className={`${td} font-bold`}>P{p.numero}</td>
                  <td className={td}>
                    <span className="font-medium text-compumaq-texto">{p.nome}</span>
                    {p.descricao && <span className="block text-xs text-compumaq-textoSuave">{p.descricao}</span>}
                    {!p.ativo && <span className="block text-xs text-red-400">Inativa (não entra na apuração)</span>}
                  </td>
                  <td className={td}>{p.tiros_minimos}</td>
                  <td className={td}>{maxUsado(p)}{p.pontos_maximos > 0 ? '' : ' (tiros × 5)'}</td>
                  <td className={td}>{p.resultados}</td>
                  <td className={`${td} text-right whitespace-nowrap`}>
                    <button className="text-xs text-compumaq-doradoClaro font-medium mr-3" onClick={() => abrir(p)}>Editar</button>
                    <button className="text-xs text-red-500 font-medium" onClick={() => setExcluir(p)}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={editando !== null} onClose={() => setEditando(null)} title={editando === 'nova' ? 'Nova pista' : 'Editar pista'}>
        <form className="space-y-3" onSubmit={(e) => {
          e.preventDefault()
          salvar.mutate({ numero: Number(form.numero), nome: form.nome, descricao: form.descricao || null, tiros_minimos: Number(form.tiros_minimos) || 0, pontos_maximos: Number(form.pontos_maximos) || 0, ativo: form.ativo })
        }}>
          <div className="grid grid-cols-4 gap-3">
            <FormField label="Nº" required><input className={inputClass} type="number" min={1} value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} aria-label="Número da pista" /></FormField>
            <div className="col-span-3"><FormField label="Nome" required><input className={inputClass} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} aria-label="Nome da pista" /></FormField></div>
          </div>
          <FormField label="Descrição / briefing"><textarea className={inputClass} rows={2} value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Tiros mínimos"><input className={inputClass} type="number" min={0} value={form.tiros_minimos} onChange={(e) => setForm({ ...form, tiros_minimos: e.target.value })} aria-label="Tiros mínimos" /></FormField>
            <FormField label="Pontos máximos" hint={`Vazio = tiros × 5${Number(form.tiros_minimos) ? ` = ${Number(form.tiros_minimos) * 5}` : ''}`}>
              <input className={inputClass} type="number" min={0} value={form.pontos_maximos} onChange={(e) => setForm({ ...form, pontos_maximos: e.target.value })} aria-label="Pontos máximos" />
            </FormField>
          </div>
          <label className="flex items-center gap-2 text-sm text-compumaq-texto">
            <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} /> Ativa (entra na apuração)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className={btnSecundario} onClick={() => setEditando(null)}>Cancelar</button>
            <button type="submit" className={btnPrimario} disabled={salvar.isPending}>{salvar.isPending ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!excluir}
        title="Excluir pista"
        message={`Excluir a pista P${excluir?.numero} — ${excluir?.nome}?`}
        confirmLabel="Excluir"
        loading={apagar.isPending}
        onConfirm={() => excluir && apagar.mutate(excluir)}
        onClose={() => setExcluir(null)}
      />
    </div>
  )
}
